import {
    SensorParameterConfig,
    SensorConfiguration,
    isNumericSensorParameterConfig,
} from "./SensorConfigurationTypes.js";
import { isDeviceVersionSelectorValid } from "../../utils/VerdiDeviceVersionUtilities.js";
import { SensorConfigurations } from "./SensorConfiguration.js";
import { initializeUnits, isUnitInitialized } from "../../utils/addMathUnits.js";
import { UnitConfigurations } from "../Units/UnitConfigurations.js";
import { UnitCategoryConfigurations } from "../Units/UnitCategory/UnitCategoryConfigurations.js";

/**
 * Helper function to throw descriptive errors for sensor configuration validation failures
 *
 * @param sensorConfig The sensor configuration that failed validation
 * @param errorMessage Detailed error message explaining the validation failure
 * @throws Error with formatted message containing sensor key and detailed error
 */
function throwSensorConfigError(sensorConfig: SensorConfiguration, errorMessage): never {
    throw new Error(
        `Verdi Types initialization Error:
    Sensor configuration for sensor "${sensorConfig.key}" is invalid
    ${errorMessage}
`,
    );
}

/**
 * Validates a sensor configuration at runtime to ensure it meets all requirements.
 *
 * This function checks:
 * - Device version selector validity
 * - Sensor parameter key consistency
 * - Sensor parameter validation
 * - Naming conventions (manufacturer name not in friendly names)
 * - Name length constraints
 *
 * @param sensorConfig The sensor configuration to validate
 * @throws Error if any validation check fails
 */
function assertValidSensorConfiguration(sensorConfig: SensorConfiguration) {
    // Only validate Verdi device sensor configurations
    if (sensorConfig.verdiDeviceSensorConfiguration) {
        // Validate device version selector format
        // This ensures the supportedDeviceVersions string follows the correct pattern (e.g., "2:4.*.*.*.*")
        if (!isDeviceVersionSelectorValid(sensorConfig.verdiDeviceSensorConfiguration.supportedDeviceVersions)) {
            throwSensorConfigError(sensorConfig, "SupportedDeviceVersions must be valid device version selector");
        }

        // Validate all sensor parameters
        // For each parameter, ensure the key in the object matches the key property of the parameter
        // and validate the parameter itself
        Object.keys(sensorConfig.sensorParameterConfigs).forEach((conversionParameterKey) => {
            const conversionParameter = sensorConfig.sensorParameterConfigs[conversionParameterKey];

            // Check for key consistency
            // The parameter's key in the object must match its internal key property
            if (conversionParameterKey !== conversionParameter.key) {
                throwSensorConfigError(
                    sensorConfig,
                    "key mismatch on conversion parameter." +
                        `conversion parameter appears at key ${conversionParameterKey} on object ` +
                        `but it's key property is ${conversionParameter.key}. ` +
                        `These should match exactly.`,
                );
            }

            // Validate the parameter's internal configuration
            assertValidSensorConversionParameter(sensorConfig, conversionParameter);
        });

        // Validate naming conventions
        // Ensure manufacturer name is not included in friendly names
        // NOTE: Disabled due to fixing these issues being out of scope
        // This prevents redundancy and keeps names cleaner
        // if(sensorConfig.friendlyName.includes(sensorConfig.manufacturerName)){
        //     throwSensorConfigError(
        //         sensorConfig,
        //         "friendlyName should not include manufacturer"
        //     )
        // }

        // Ensure manufacturer name is not included in short friendly names
        // if(sensorConfig.friendlyShortName.includes(sensorConfig.manufacturerName)){
        //     throwSensorConfigError(
        //         sensorConfig,
        //         "friendlyShortName should not include manufacturer"
        //     )
        // }

        // Enforce length constraints on short friendly names
        // Short names should be concise for UI display purposes
        if (sensorConfig.friendlyShortName.length > 25) {
            throwSensorConfigError(
                sensorConfig,
                "friendlyShortName should try to stay below 15 characters, " +
                    "but must stay below 25 characters. " +
                    `"${sensorConfig.friendlyShortName}" is ${sensorConfig.friendlyShortName.length} characters`,
            );
        }
    }
}

/**
 * Validates a sensor parameter configuration at runtime.
 *
 * This function checks:
 * - Default value passes the validator
 * - All option values pass the validator
 * - Default value is one of the options (if options are specified)
 * - For numeric parameters:
 *   - mustBeNonNegative is enforced by validator
 *   - mustBeInteger is enforced by validator
 *   - unit is a valid math.js unit
 *   - alternative units are valid and compatible with the base unit
 *
 * @param sensorConfig The parent sensor configuration
 * @param conversionParameter The parameter configuration to validate
 * @throws Error if any validation check fails
 */
function assertValidSensorConversionParameter(
    sensorConfig: SensorConfiguration,
    conversionParameter: SensorParameterConfig,
) {
    // Helper function to throw errors with context about which parameter failed
    const throwError = (message: string) => {
        throwSensorConfigError(
            sensorConfig,
            `Error in sensor conversion parameter ${conversionParameter.key}\n` + message,
        );
    };

    // Validate default value
    // The validator function should accept the default value
    // Note: validator returns [true, ""] if valid, [false, errorMessage] if invalid
    // So we check if the first element is false (valid)
    if (!conversionParameter.validator(conversionParameter.defaultValue)[0]) {
        throwError(
            `validator should accept the default value for a conversion parameter (default value: ${conversionParameter.defaultValue})`,
        );
    }

    // Validate value options (if specified)
    if (conversionParameter.valueOptions) {
        // Ensure all option values pass the validator
        conversionParameter.valueOptions.forEach((option) => {
            if (!conversionParameter.validator(option.value)[0]) {
                throwError(
                    `validator should accept any specified options values for a conversion parameter ` +
                        `(option with value "${option.value} was not accepted)`,
                );
            }
        });

        // Ensure default value is one of the specified options
        if (!conversionParameter.valueOptions.some((option) => option.value === conversionParameter.defaultValue)) {
            throwError(
                `if options are specified for a conversion parameter, the default value should ` +
                    `be one of the included options` +
                    `(no option with value "${conversionParameter.defaultValue}" was found)`,
            );
        }
    }

    // Additional validations for numeric parameters
    if (isNumericSensorParameterConfig(conversionParameter)) {
        // Ensure mustBeNonNegative flag is enforced by validator
        // If mustBeNonNegative is true, validator should reject negative values
        if (conversionParameter.mustBeNonNegative && conversionParameter.validator(-1)[0]) {
            throwError("If mustBePositive is true, validator should not accept negative values");
        }

        // Ensure mustBeInteger flag is enforced by validator
        // If mustBeInteger is true, validator should reject decimal values
        if (conversionParameter.mustBeInteger && conversionParameter.validator(0.1)[0]) {
            throwError("If mustBeInteger is true, validator should not accept floating point values");
        }

        // Validate provided sensor parameter unit category maps to a recognized math.js unit
        const databaseUnit = UnitCategoryConfigurations[conversionParameter.parameterUnitCategory].databaseUnit;
        if (!isUnitInitialized(UnitConfigurations[databaseUnit].mathjs)) {
            throwError(
                `unit "${UnitConfigurations[databaseUnit].mathjs}" is not a math.js unit. ` +
                    `If this is a custom unit, ` +
                    `add it in the file addMathUnits.ts. ` +
                    `If this is not a custom unit, make sure the casing is correct`,
            );
        }
    }
}

/**
 * Validates all sensor configurations in the system.
 *
 * This function initializes math units and then validates each sensor configuration
 * to ensure they all meet the required standards. It should be called during system
 * initialization to catch configuration errors early.
 *
 * The validation process:
 * - Initializes math units to ensure all unit conversions work properly
 * - Iterates through all sensor configurations
 * - Validates each configuration using assertValidSensorConfiguration
 *
 * @throws Error if any sensor configuration fails validation
 */
export function assertValidSensorConfigs() {
    // Initialize math units
    // This ensures all custom units are registered with math.js before validation
    initializeUnits();

    // Validate each sensor configuration
    // This catches any errors in the sensor configuration definitions
    Object.values(SensorConfigurations).forEach((sensorConfig) => {
        assertValidSensorConfiguration(sensorConfig);
    });
}
